import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { getWpImageSrc } from '../../../lib/wpMedia';
import { ArrowRight } from 'lucide-react';
import { CAREERS_COPY } from '../../../constants/careersTranslations';

export function CareersTeamSection({ posts = [], lang = 'en', copy }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const t = copy?.team ?? CAREERS_COPY[lang]?.team ?? CAREERS_COPY.en.team;
  const blogBase = lang === 'fi' ? '/blogi' : '/blog';

  return (
    <section ref={ref} className="section-rhythm bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="heading-2 mb-4 text-gray-900 text-center"
        >
          {t.title}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-12"
        >
          {t.subtitle}
        </motion.p>
        {posts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <motion.article
                key={post.slug ?? post.href ?? index}
                initial={{ opacity: 0, y: 24 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.08 + index * 0.06 }}
                className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-[0_8px_24px_-6px_rgba(0,0,0,0.08)] transition-shadow"
              >
                <Link
                  href={post.href ?? (post.slug ? `${blogBase}/${post.slug}` : blogBase) ?? '/'}
                  className="block cursor-pointer no-underline touch-manipulation"
                >
                  <div className="h-48 bg-gray-100 relative overflow-hidden">
                    <Image
                      src={(getWpImageSrc(post.image) || post.image) ?? '/blog-placeholders/blog-1.png'}
                      alt={post.title ?? ''}
                      fill
                      unoptimized
                      sizes="(max-width: 768px) 100vw, 33vw"
                      className="object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 leading-tight line-clamp-2">
                      {post.title}
                    </h3>
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-brand">
                      {t.readMore}
                      <ArrowRight className="w-4 h-4 shrink-0" />
                    </span>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        ) : (
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center text-gray-500"
          >
            {t.empty}
          </motion.p>
        )}
        {posts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-10 text-center"
          >
            <Link
              href={t.viewAllHref ?? blogBase}
              className="group inline-flex items-center gap-2 text-brand font-semibold hover:text-brand-hover hover:gap-3 transition-all touch-manipulation"
            >
              {t.viewAll}
              <ArrowRight className="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:translate-x-1" aria-hidden />
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
}
